import {
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
  Flex,
  IconButton,
  Checkbox,
} from '@chakra-ui/react'
import { CloseIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { useFeatureFlagPayload } from 'posthog-js/react'
import type { ReleaseAnnouncementFeature, ReleaseAnnouncementPayload } from './types'
import {
  formatPublishedAt,
  isAnnouncementPermanentlyDismissed,
  isReleaseAnnouncementPayload,
  persistAnnouncementPermanentDismiss,
  resolveFeatureIcon,
  resolveFeatureTheme,
  WHATS_NEW_MODAL_FLAG_KEY,
} from './utils'

const FeatureAnnouncementModal: FunctionComponent = () => {
  const flagPayload = useFeatureFlagPayload(WHATS_NEW_MODAL_FLAG_KEY)
  const [payload, setPayload] = useState<ReleaseAnnouncementPayload | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const autoOpenedIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isReleaseAnnouncementPayload(flagPayload)) return

    const announcement = flagPayload
    if (announcement.enabled === false) return

    if (isAnnouncementPermanentlyDismissed(announcement.id)) return
    if (autoOpenedIdsRef.current.has(announcement.id)) return

    autoOpenedIdsRef.current.add(announcement.id)
    setDontShowAgain(false)
    setPayload(announcement)
    setIsOpen(true)
  }, [flagPayload])

  const handleClose = useCallback(() => {
    setIsOpen(false)

    if (dontShowAgain && payload?.id) {
      persistAnnouncementPermanentDismiss(payload.id)
    }
  }, [dontShowAgain, payload?.id])

  const handleGotIt = useCallback(() => {
    if (!payload?.id) {
      setIsOpen(false)
      return
    }

    setIsOpen(false)
    persistAnnouncementPermanentDismiss(payload.id)
  }, [payload?.id])

  if (!isOpen || !payload) return null

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        borderRadius="xl"
        overflow="hidden"
        maxW="640px"
        maxH="90vh"
        mx={4}
        display="flex"
        flexDirection="column"
      >
        <Box
          flexShrink={0}
          bg="#0D825B"
          color="white"
          px={8}
          pt={6}
          pb={8}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-80px"
            right="-40px"
            width="250px"
            height="250px"
            borderRadius="full"
            bg="whiteAlpha.100"
            pointerEvents="none"
          />

          <Flex justify="space-between" align="center" mb={6} position="relative" zIndex={1}>
            <HStack spacing={3}>
              <Box
                px={3}
                py={1}
                borderRadius="full"
                border="1px solid"
                borderColor="whiteAlpha.300"
                bg="whiteAlpha.100"
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="wide"
              >
                {payload.header.badgeLabel}
              </Box>
            </HStack>
            <HStack spacing={4}>
              <Text fontSize="sm" color="whiteAlpha.800" fontWeight="medium">
                {formatPublishedAt(payload.publishedAt)}
              </Text>
              <IconButton
                aria-label="Close"
                icon={<CloseIcon boxSize={3} />}
                size="sm"
                isRound
                bg="whiteAlpha.200"
                _hover={{ bg: 'whiteAlpha.300' }}
                color="white"
                onClick={handleClose}
              />
            </HStack>
          </Flex>

          <Box position="relative" zIndex={1}>
            <Text fontSize="2xl" fontWeight="bold" mb={2}>
              {payload.header.title}
            </Text>
          </Box>
        </Box>

        <Box flex="1" minH={0} overflowY="auto" px={8} py={6}>
          <VStack spacing={4} align="stretch">
            {payload.features.map((feature: ReleaseAnnouncementFeature) => {
              const theme = resolveFeatureTheme(feature.theme)
              const FeatureIcon = resolveFeatureIcon(feature.icon)

              return (
                <Flex
                  key={feature.id}
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  align="flex-start"
                  gap={4}
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={theme.iconBg}
                    color={theme.iconColor}
                    w={12}
                    h={12}
                    borderRadius="lg"
                    flexShrink={0}
                  >
                    <Icon as={FeatureIcon} boxSize={5} />
                  </Flex>
                  <Box flex={1} pt={0.5}>
                    <Text fontWeight="bold" color="gray.800" fontSize="md" mb={1}>
                      {feature.title}
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      {feature.description}
                    </Text>
                  </Box>
                  {feature.badgeLabel ? (
                    <Box
                      px={2}
                      py={1}
                      bg="green.50"
                      color="green.600"
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {feature.badgeLabel}
                    </Box>
                  ) : null}
                </Flex>
              )
            })}
          </VStack>
        </Box>

        <Flex
          flexShrink={0}
          px={8}
          py={5}
          borderTop="1px solid"
          borderColor="gray.100"
          justify="space-between"
          align="center"
          bg="white"
        >
          <Checkbox
            size="md"
            colorScheme="green"
            color="gray.500"
            fontWeight="medium"
            isChecked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          >
            {payload.footer.dismissCheckboxLabel}
          </Checkbox>
          <Button
            bg="#0D825B"
            color="white"
            _hover={{ bg: '#0a6848' }}
            rightIcon={<ArrowForwardIcon />}
            onClick={handleGotIt}
            borderRadius="md"
            px={6}
            fontWeight="bold"
          >
            {payload.footer.primaryButtonLabel}
          </Button>
        </Flex>
      </ModalContent>
    </Modal>
  )
}

export default FeatureAnnouncementModal
